// src/components/home/LocationMapCard.jsx
import React, { useEffect, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polygon,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { MapPin } from "lucide-react";
import { useGeolocated } from "react-geolocated";
import { usePermissions } from "../../context/PermissionContext";
import { pointInPolygon } from "../../utils/PointInPolygon";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom red icon for current location
const currentLocationIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Component to auto-center on current location
const AutoCenterOnLocation = ({ coords }) => {
  const map = useMap();
  const hasCentered = useRef(false);

  useEffect(() => {
    if (coords?.latitude && coords?.longitude && !hasCentered.current) {
      map.flyTo([coords.latitude, coords.longitude], 16, {
        duration: 1
      });
      hasCentered.current = true;
    }
  }, [coords, map]);

  return null;
};

// Custom component to fit map bounds to current polygon
const FitToCurrentPolygon = ({
  currentLocation,
  coords,
  assignedLocations,
}) => {
  const map = useMap();

  useEffect(() => {
    if (
      currentLocation &&
      coords?.latitude &&
      coords?.longitude &&
      assignedLocations.length > 0
    ) {
      const currentPolyPositions = currentLocation.polygons.map((p) => [
        p.lat,
        p.lng,
      ]);
      const bounds = L.latLngBounds(currentPolyPositions);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [currentLocation, coords, assignedLocations, map]);

  return null;
};

const LocationMapCard = () => {
  const { assignedLocations } = usePermissions();
  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
      },
      userDecisionTimeout: 10000,
    });

  // Determine current location status
  const getCurrentLocation = () => {
    if (!isGeolocationAvailable) {
      return {
        name: "Geolocation not supported by this browser.",
        location: null,
      };
    }
    if (!isGeolocationEnabled) {
      return {
        name: "Geolocation is disabled. Please enable location services.",
        location: null,
      };
    }
    if (!coords?.latitude || !coords?.longitude) {
      return { name: "Fetching location...", location: null };
    }

    const userPoint = { lat: coords.latitude, lng: coords.longitude };
    const currentLocationObj = assignedLocations.find((location) =>
      pointInPolygon(userPoint, location.polygons)
    );

    return currentLocationObj
      ? { name: currentLocationObj.location_name, location: currentLocationObj }
      : { name: "Not in an assigned location", location: null };
  };

  const currentLocationResult = getCurrentLocation();
  const { name: currentLocationName, location: currentLocation } =
    currentLocationResult;

  const assignedLocationsList = Array.isArray(assignedLocations)
    ? assignedLocations
    : [];

  // Directly set map center to current location
  const mapCenter =
    coords?.latitude && coords?.longitude
      ? [coords.latitude, coords.longitude]
      : [51.505, -0.09];

  // Memoize all polygons for rendering
  const allPolygons = useMemo(() => {
    return assignedLocationsList.map((location) => ({
      positions: location.polygons.map((p) => [p.lat, p.lng]),
      name: location.location_name,
      isCurrent:
        location.location_id === (currentLocation?.location_id || null),
    }));
  }, [assignedLocationsList, currentLocation]);

  return (
    <div className="card shadow-sm">
      <div className="card-body p-5">
        <p className="fw-bold mb-4">Location Map</p>

        {/* Current Location */}
        <div className="d-flex align-items-center mb-3 gap-3">
          <MapPin size={20} className="" />
          <div>
            <p className="mb-0">Current Location</p>
            <small className="">{currentLocationName}</small>
          </div>
        </div>

        {/* Map */}
        <div
          className="map-container"
          style={{ height: "300px", width: "100%", position: "relative", zIndex: 1 }}
        >
          <MapContainer
            center={mapCenter}
            zoom={16} // Zoom level 16 for close view
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {/* Auto center on current location */}
            <AutoCenterOnLocation coords={coords} />

            {/* Fit to Current Polygon */}
            <FitToCurrentPolygon
              currentLocation={currentLocation}
              coords={coords}
              assignedLocations={assignedLocationsList}
            />

            {/* User's Current Location Marker */}
            {coords?.latitude && coords?.longitude && (
              <Marker 
                position={[coords.latitude, coords.longitude]}
                icon={currentLocationIcon}
                eventHandlers={{
                  click: (e) => {
                    const map = e.target._map;
                    map.flyTo([coords.latitude, coords.longitude], 18, {
                      duration: 0.5
                    });
                  }
                }}
              >
                <Popup>
                  <div className="text-center">
                    <strong>Your Current Location</strong><br/>
                    Click marker to zoom in
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Assigned Location Polygons */}
            {allPolygons.map((poly, index) => (
              <Polygon
                key={index}
                positions={poly.positions}
                pathOptions={{
                  color: poly.isCurrent ? "#E63C23" : "black",
                  fillOpacity: poly.isCurrent ? 0.3 : 0.2,
                }}
              >
                <Popup>{poly.name}</Popup>
              </Polygon>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default LocationMapCard;